import {
    FC,
    ReactElement,
    useEffect,
} from 'react'
import { useParams } from 'react-router'

import { Card, InfoListItem, TimeTicker } from '@/components'
import { TrackLogs } from '@/compositions'
import { convertDateToDateString, convertDateToDay, convertDateToTime } from '@/helpers/DateTimeParser'
import { shouldShowEndTime } from '@/helpers/tools'
import { trans } from '@/helpers/trans'
import { TrackResource } from '@/models'
import { SettingResource } from '@/models/Setting'
import { useTypedDispatch, useTypedSelector } from '@/redux/store'
import { getTrack } from '@/redux/track/trackActions'

import './TracksDetailPage.scss'

const getTrackInformation = (track: TrackResource): ReactElement | string => {
    if (! track.billableEnd) {
        return <TimeTicker from={track.checkIn} />
    }

    return `${convertDateToTime(track.billableStart)} - ${convertDateToTime(track.billableEnd)}`
}

const getShiftTime = (track: TrackResource, settings: SettingResource[]): string | null => {
    if (! track.shift) {
        return null
    }

    const showEndTime = shouldShowEndTime(settings, track.shift.department?.id)

    if (track.shift.end && showEndTime) {
        return `${convertDateToTime(track.shift.start)} - ${convertDateToTime(track.shift.end)}`
    }

    return convertDateToTime(track.shift.start)
}

export const TracksDetailPage: FC = (): ReactElement | null => {
    const dispatch = useTypedDispatch()
    const { trackUuid } = useParams()
    const { track } = useTypedSelector(state => state.trackReducer)
    const { settings } = useTypedSelector(state => state.userReducer)

    useEffect(() => {
        if (trackUuid) {
            dispatch(getTrack(trackUuid))
        }
    }, [])

    if (! track) {
        return null
    }

    const feedback = track.feedback?.rating
    const feedbackRemark = track.feedback?.remark
    const shiftTime = getShiftTime(track, settings)

    return (
        <>
            <Card className="tracks-detail__header">
                <h1 className="tracks-detail__title">
                    {getTrackInformation(track)}
                </h1>
                <span className="tracks-detail__date">
                    {`${convertDateToDay(track.checkIn)} ${convertDateToDateString(track.checkIn)}`}
                </span>
            </Card>

            <ul className="mb-4 border-top">
                {track.shift && (
                    <>
                        <InfoListItem label={trans('common.department')}>
                            {track.shift.department.name}
                        </InfoListItem>
                        <InfoListItem label={trans('common.shiftTime')}>
                            {shiftTime}
                        </InfoListItem>
                    </>
                )}
                <InfoListItem label={trans('pages.shifts.clockedTime')}>
                    <span className={track.billableEnd ? '' : 'text-danger'}>{getTrackInformation(track)}</span>
                </InfoListItem>
            </ul>

            {track.billableEnd && (
                <>
                    <ul className="mb-4 border-top">
                        <InfoListItem label={trans('pages.shifts.workedTime')}>
                            {track.totalBillableTime}
                        </InfoListItem>
                    </ul>

                    <ul className="mb-4 border-top">
                        <InfoListItem label={trans('common.break')}>
                            {`${track.pause} ${trans('common.minutes')}`}
                        </InfoListItem>
                        <InfoListItem label={trans('common.meal')}>
                            {track.meal ? trans(`pages.tracks.${track.meal.toLowerCase()}`) : ''}
                        </InfoListItem>
                        {feedback && (
                            <InfoListItem label={trans('common.satisfaction')}>
                                {trans(`common.${feedback.toLowerCase()}`)}
                            </InfoListItem>
                        )}
                        {feedbackRemark && (
                            <InfoListItem label={`${trans('common.reason')} ${trans('common.dissatisfaction').toLowerCase()}`}>
                                {feedbackRemark}
                            </InfoListItem>
                        )}
                    </ul>
                </>
            )}

            {track.logs && <TrackLogs logs={track.logs} />}
        </>
    )
}
